import { useEffect, useState } from "react";
import { History, Trash2, ArrowUpRight, ArrowDownRight, Minus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, CATEGORY_COLORS } from "@/lib/api";
import { t, locale } from "@/lib/i18n";

const PALETTE = ["#E67E22", "#3FB68B", "#5B8DEF", "#E4B04A", "#C25B8A", "#8ED1FC", "#B7A57A", "#F06A6A", "#7ED957", "#A78BFA"];

const MixChart = ({ evolution }) => {
  const [hidden, setHidden] = useState({});
  if (evolution.periods.length < 2) return null;
  const data = evolution.periods.map((p, i) => ({ period: p.period_label, ...Object.fromEntries(evolution.rows.map((r) => [r.name, r.cells[i]?.mix_pct ?? null])) }));
  const toggle = (name) => setHidden((h) => ({ ...h, [name]: !h[name] }));
  return (
    <div data-testid="sales-mix-chart" className="surface-elevated p-3">
      <div className="flex items-center gap-2 mb-2"><TrendingUp size={13} className="text-primary" /><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("Mix di vendita per piatto nel tempo (%)")}</div></div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -12 }}>
            <CartesianGrid stroke="#2E2A26" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="period" stroke="#6E675F" tick={{ fontSize: 11 }} />
            <YAxis stroke="#6E675F" tick={{ fontSize: 11 }} unit="%" />
            <Tooltip contentStyle={{ background: "#221F1C", border: "1px solid #2E2A26", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v}%`]} />
            {evolution.rows.map((r, i) => !hidden[r.name] && <Line key={r.dish_id} type="monotone" dataKey={r.name} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
        {evolution.rows.map((r, i) => (
          <button key={r.dish_id} data-testid={`sales-mix-legend-${r.dish_id}`} onClick={() => toggle(r.name)} className={`flex items-center gap-1.5 text-[11px] transition-opacity ${hidden[r.name] ? "opacity-35 line-through" : ""}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{r.name}
          </button>
        ))}
      </div>
    </div>
  );
};

const SHORT = { stella: "Stella", cavallo: "Cavallo", enigma: "Enigma", cane: "Cane" };
const RANK = { stella: 3, cavallo: 2, enigma: 1, cane: 0 };
const fmtDate = (s) => new Date(s).toLocaleDateString(locale(), { day: "2-digit", month: "short" });

const Cell = ({ cell, prev }) => {
  if (!cell) return <span className="text-muted-foreground/50">—</span>;
  const changed = prev && prev.category !== cell.category;
  const up = changed && RANK[cell.category] > RANK[prev.category];
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: CATEGORY_COLORS[cell.category] }} title={`${cell.sold} ${t("venduti")} · mix ${cell.mix_pct}%`}>
      {changed && (up ? <ArrowUpRight size={12} className="text-emerald-400" /> : <ArrowDownRight size={12} className="text-amber-300" />)}
      {t(SHORT[cell.category])}
    </span>
  );
};

export const SalesHistory = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/dishes/sales/history").then((r) => setData(r.data));
  useEffect(() => { load(); }, [refreshKey]);
  const del = async (id) => {
    try { await api.delete(`/dishes/sales/history/${id}`); toast.success(t("Periodo rimosso dallo storico")); load(); }
    catch { toast.error(t("Rimozione non riuscita")); }
  };
  if (!data) return null;
  const { imports, evolution } = data;

  return (
    <section data-testid="sales-history" className="surface p-4 space-y-4">
      <div className="flex items-center gap-2"><History size={15} className="text-primary" /><div className="eyebrow">{t("Storico vendite · come cambia la classe di ogni piatto")}</div><span data-testid="sales-history-count" className="num text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary">{imports.length}</span></div>
      {imports.length === 0 ? (
        <p data-testid="sales-history-empty" className="text-xs text-muted-foreground">{t("Nessun import ancora salvato. Ogni volta che applichi un export della cassa, il periodo viene conservato qui con la fotografia della matrice.")}</p>
      ) : (
        <div className="grid lg:grid-cols-[300px_1fr] gap-4">
          <ul className="space-y-1.5">
            {imports.map((i, idx) => (
              <li key={i.id} data-testid={`sales-history-period-${idx}`} className="surface-elevated px-3 py-2 flex items-start gap-2 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{i.period_label}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{fmtDate(i.created_at)} · {i.total_qty ?? "—"} {t("porzioni")} · {i.updated} {t("aggiornati")}, {i.created} {t("nuovi")}{i.filename ? ` · ${i.filename}` : ""}</div>
                  {i.summary && <div className="flex gap-2 mt-1">{Object.keys(SHORT).map((k) => <span key={k} className="num text-[10px]" style={{ color: CATEGORY_COLORS[k] }}>{i.summary[k] ?? 0} {t(SHORT[k]).toLowerCase()}</span>)}</div>}
                </div>
                <button data-testid={`sales-history-delete-${i.id}`} onClick={() => del(i.id)} className="text-muted-foreground hover:text-destructive p-1" title={t("Rimuovi periodo")}><Trash2 size={13} /></button>
              </li>
            ))}
          </ul>
          <div className="overflow-x-auto">
            <table data-testid="sales-evolution-table" className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border"><th className="text-left p-2">{t("Piatto")}</th>{evolution.periods.map((p) => <th key={p.id} className="text-left p-2 whitespace-nowrap">{p.period_label}</th>)}<th className="text-right p-2">{t("Cambi")}</th></tr>
              </thead>
              <tbody>
                {evolution.rows.map((r) => (
                  <tr key={r.dish_id} data-testid={`sales-evolution-row-${r.dish_id}`} className="border-b border-border last:border-0">
                    <td className="p-2 font-medium whitespace-nowrap">{r.name}</td>
                    {r.cells.map((c, i) => <td key={i} className="p-2"><Cell cell={c} prev={i > 0 ? r.cells[i - 1] : null} /></td>)}
                    <td className="p-2 text-right num text-xs">{r.changes > 0 ? <span className={r.last_change === "up" ? "text-emerald-400" : "text-amber-300"}>{r.changes}</span> : <Minus size={12} className="inline text-muted-foreground/50" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">{t("Freccia verde in su: il piatto è salito di classe rispetto al periodo precedente; in giù: è sceso. Passa il mouse su una classe per vedere venduti e mix.")}</p>
          </div>
        </div>
      )}
      {imports.length > 0 && <MixChart evolution={evolution} />}
    </section>
  );
};
